import React, { useRef, useState } from "react";

import styled from "@emotion/styled";
import Text from "@components/Text";
import { useStores } from "@stores";
import { observer } from "mobx-react-lite";
import useOnClickOutside from "@src/hooks/useOnClickOutside";
import { useLang } from "@src/hooks/useLang";
import {
  getInitials,
  Avatar,
  RoleBadge,
  membershipsWithSelf,
  canSwitchAccounts,
} from "@components/AccountSwitcher/shared";

interface IProps {
  expanded: boolean;
  menuWidth: number;
}

const Root = styled.div`
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  padding: 0 0 0 16px;
  height: 38px;
  overflow: hidden;
  white-space: nowrap;
  flex-wrap: nowrap;
  text-overflow: ellipsis;
  color: #899093;
  svg {
    min-width: 20px;
  }
  path {
    fill: #899093;
  }
  position: relative;
`;

const ContentContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  overflow: hidden;
`;

const AccountInfo = styled.div`
  margin-left: 12px;
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const AccountName = styled(Text)`
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;

const AccountEmail = styled(Text)`
  color: #899093;
  font-weight: normal;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;

// Replaces the email line while this tab is impersonating a user (admin support
// tool). Clicking it ends impersonation — this is the only exit control now that
// the floating banner is gone, so it stays visually distinct from normal text.
const ImpersonationLabel = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 10px;
  font-weight: bold;
  color: #d99b00;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  cursor: pointer;
  white-space: nowrap;
  :hover {
    text-decoration: underline;
  }
`;

const Popover = styled.div`
  position: absolute;
  bottom: 46px;
  left: 8px;
  right: 8px;
  min-width: 220px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.16);
  padding: 6px;
  z-index: 3000;
  max-height: 320px;
  overflow: auto;
`;

const PopoverItem = styled.div<{ active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  background: ${({ active }) => (active ? "#f2f7ec" : "transparent")};
  :hover {
    background: #f8f8f8;
  }
`;

const AccountSwitcher: React.FC<IProps> = ({ expanded }) => {
  const { accountStore } = useStores();
  const lang = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => setOpen(false));

  if (!accountStore.account) {
    return null;
  }

  const self = accountStore.account;
  const active = accountStore.activeAccount;
  const viewingOther = accountStore.isMember;
  const t = (lang as any).team ?? {};

  const memberships = membershipsWithSelf(accountStore);

  // What to display as the "current" account in the sidebar.
  const displayName = active && !active.isSelf ? active.name ?? active.email : self.name;
  const displayEmail = active && !active.isSelf ? active.email : self.email;

  const canOpen = canSwitchAccounts(accountStore);

  return (
    <div style={{ position: "relative", width: "100%" }} ref={ref}>
      {open && (
        <Popover>
          {memberships.map((m) => (
            <PopoverItem
              key={m.accountId}
              active={m.accountId === accountStore.activeAccountId}
              onClick={() => {
                setOpen(false);
                accountStore.setActiveAccountId(m.accountId);
              }}
            >
              <Avatar size={26} muted={!m.isSelf}>
                {getInitials(m.name ?? m.email)}
              </Avatar>
              <div style={{ overflow: "hidden", flex: 1 }}>
                <Text size={13} type="heading" color="#494C5B" nowrap>
                  {m.isSelf ? t.myAccount ?? "My account" : m.name ?? m.email}
                </Text>
                <Text size={11} color="#899093" nowrap>
                  {m.isSelf ? m.email : m.groupName ?? m.email}
                </Text>
              </div>
              {!m.isSelf && <RoleBadge>{t.memberBadge ?? "Member"}</RoleBadge>}
            </PopoverItem>
          ))}
        </Popover>
      )}
      <Root onClick={() => canOpen && setOpen((v) => !v)}>
        <Avatar size={20} muted={viewingOther}>
          {getInitials(displayName ?? "")}
        </Avatar>
        {expanded && (
          <ContentContainer>
            <AccountInfo>
              <AccountName size={12}>{displayName}</AccountName>
              {accountStore.impersonating ? (
                <ImpersonationLabel
                  title="Click to exit impersonation"
                  onClick={(e) => {
                    e.stopPropagation();
                    accountStore.exitImpersonation();
                  }}
                >
                  Impersonation
                </ImpersonationLabel>
              ) : (
                <AccountEmail size={10}>
                  {viewingOther ? t.viewingLabel ?? "Viewing" : displayEmail}
                  {viewingOther ? ` · ${displayEmail}` : ""}
                </AccountEmail>
              )}
            </AccountInfo>
          </ContentContainer>
        )}
      </Root>
    </div>
  );
};

export default observer(AccountSwitcher);
